import React from 'react';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { ParsedUrlQuery } from 'querystring';
import * as seline from '@seline-analytics/web';

import PageLayout from '../../components/blocks/page-layout';
import Products from '../../components/blocks/products';
import Title from '../../components/elements/title';
import graphqlClient from '../../utils/graphql-client';
import { captureException } from '../_app';
import { CategoryObjectType, ProductObjectType } from '../../../generated/graphql';
import { SHORT_INVALIDATION_TIME } from '../../constants';
import useBrowserEffect from '../../hooks/useBrowserEffect';
import { BackLink } from '../../components/blocks/back-link';
import { sortProductsByAvailability } from '../../utils/products';
import { useTranslate } from '../../hooks/useTranslate';
import { useLocale } from '../../hooks/useLocale';

interface Props {
  products?: ProductObjectType[] | null;
  selectedCategory?: CategoryObjectType | null;
  error?: Error | null;
  query: ParsedUrlQuery;
  isOutlet?: boolean | null;
  isSale?: boolean | null;
}

const categoryQueryFragment = `category(id: $category) {
  _id
  picture
  name {
    nl
    fr
  }
}`;

const getProductsQuery = (category?: string) => {
  return `query getProducts($hasAnyDiscount: Boolean, $category: ObjectId${category ? '!' : ''}) {
    allProductsInStore(inStock: false, hidePrivate: true, category: $category, hasAnyDiscount: $hasAnyDiscount) {
      _id
      name {
        nl
        fr
      }
      discount
      pictures
      price
      isOutlet
      isSale
      availableAt
      stock {
        size
        displaySize
        amount
      }
    }

    ${category ? categoryQueryFragment : ''}
  }`;
};

export default function Page(props: Props) {
  const { products, selectedCategory, isOutlet, isSale } = props;
  const translate = useTranslate();
  const locale = useLocale();

  let categoryName = translate('allClothing');
  if (isOutlet || isSale) {
    categoryName = 'Sales';
  } else if (selectedCategory && selectedCategory.name) {
    categoryName = selectedCategory.name[locale];
  }

  useBrowserEffect(() => {
    seline.track('View products', {
      categoryName: categoryName,
    });
  });

  return (
    <PageLayout title={categoryName} image={selectedCategory?.picture}>
      <BackLink pageName={translate('categories')} href="/categories" />
      <Title>{categoryName}</Title>
      <Products products={products || []} />
    </PageLayout>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext): Promise<GetServerSidePropsResult<Props>> {
  const { res } = ctx;

  console.log(ctx);
  console.log(ctx.query);

  let query: any = ctx.query;
  let isOutlet = query.isOutlet ? true : null;
  let isSale = query.isSale ? true : null;
  let hasAnyDiscount = Boolean(query.isSale ?? query.isOutlet) || null;

  let variables = {
    category: query.category,
    hasAnyDiscount,
  };

  let products = null;
  let category = null;
  let error: Error | null = null;
  try {
    let res = await graphqlClient.request<{
      allProductsInStore: ProductObjectType[];
      category: CategoryObjectType;
    }>(getProductsQuery(query.category), variables);

    products = sortProductsByAvailability(res.allProductsInStore);
    category = res.category || null;
  } catch (err: any) {
    captureException(err);
    error = err;
  }

  res.setHeader('Cache-Control', `s-maxage=${SHORT_INVALIDATION_TIME}, stale-while-revalidate`);

  return {
    props: {
      selectedCategory: category,
      products,
      error,
      query,
      isOutlet,
      isSale,
    },
  };
}

export const runtime = 'experimental-edge';
